// Family / kids API surface for the parent shell. Wraps /family/kids/*.

import { api } from './api';

export type Grade = 'kg2' | 'g1' | 'g2' | 'g3' | 'g4' | 'g5' | 'g6';
export type TashkeelMode = 'with' | 'partial' | 'without';

export interface Kid {
  id: string;
  username: string;
  displayName: string | null;
  avatar: string | null;
  dob: string | null;
  grade: string | null;
  localePref: 'ar' | 'en';
  tashkeelMode: string;
  createdAt: string;
}

export async function listKids(signal?: AbortSignal): Promise<Kid[]> {
  const { kids } = await api<{ kids: Kid[] }>('/family/kids', { signal });
  return kids;
}

export interface CreateKidInput {
  username: string;
  password: string;
  displayName: string;
  avatar: string;
  grade?: Grade | null;
  localePref?: 'ar' | 'en';
  tashkeelMode?: TashkeelMode;
}

export async function createKid(input: CreateKidInput): Promise<Kid> {
  const { kid } = await api<{ kid: Kid }>('/family/kids', { method: 'POST', body: input });
  return kid;
}

export interface UpdateKidInput {
  displayName?: string;
  avatar?: string;
  grade?: Grade | null;
  localePref?: 'ar' | 'en';
  tashkeelMode?: TashkeelMode;
}

export async function updateKid(id: string, input: UpdateKidInput): Promise<Kid> {
  const { kid } = await api<{ kid: Kid }>(`/family/kids/${id}`, { method: 'PATCH', body: input });
  return kid;
}

export async function resetKidPassword(id: string, password: string): Promise<void> {
  await api(`/family/kids/${id}/reset-password`, { method: 'POST', body: { password } });
}

export async function deleteKid(id: string): Promise<void> {
  await api(`/family/kids/${id}`, { method: 'DELETE' });
}

export async function quickLaunchKid(id: string): Promise<void> {
  await api(`/family/kids/${id}/quick-launch`, { method: 'POST' });
}

// ─── Username availability ──────────────────────────────────────────────────

export interface UsernameAvailability {
  candidate: string;
  available: boolean;
  suggestions: string[];
  reason?: string;
}

export async function checkUsername(
  candidate: string,
  signal?: AbortSignal,
): Promise<UsernameAvailability> {
  return api<UsernameAvailability>(
    `/auth/username/availability?candidate=${encodeURIComponent(candidate)}`,
    { signal },
  );
}
